import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { GenderFilter, MarketableFilter, UserPackFilter } from '../components/advancedFilterTypes';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Popover,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  Business,
  Cancel,
  CheckCircle,
  Delete,
  Edit,
  FileDownload,
  FilterList,
  LocalOffer,
  MoreVert,
  People,
  PersonAdd,
  Search,
  ViewColumn,
  Visibility,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/theme';
import AdvancedFiltersPopover, { AdvancedFilterSection } from '../components/AdvancedFiltersPopover';
import {
  EMPTY_USER_ADVANCED_FILTERS,
  UserAdvancedFilters,
} from '../components/advancedFilterTypes';
import UserFormDialog from '../components/UserFormDialog';
import UserNoteDialog, { noteMenuLabel, notesModeFor, useUserNotes } from '../components/UserNoteDialog';
import { currentNoteStudioId } from '../services/userNotesApi';
import ManualBookingDialog from '../components/ManualBookingDialog';
import ManualPackAssignDialog from '../components/ManualPackAssignDialog';
import {
  emptyUserRemoveWarnings,
  RemoveUserConfirmMessage,
  UserRemoveWarnings,
} from '../components/RemoveUserConfirmMessage';
import {
  AdminUser,
  QuickTabValue,
  USER_COLUMN_LABELS,
  UserColumnKey,
  UserPackStatus,
  computeUserStats,
  deriveCityOptions,
  derivePlatformOptions,
  formatJoinedDate,
  getAvatarColor,
  getPlatformDisplay,
  getRemovedByLabel,
  getUserInitials,
  isUserMarketable,
  loadVisibleColumns,
  saveVisibleColumns,
  userMatchesPackFilter,
  userMatchesPlatform,
} from '../utils/userHelpers';
import { buildCurrentPath } from '../utils/backNavigation';
import { isStudioScopedRole } from '../utils/roles';
import BranchSelect from '../components/BranchSelect';
import { useMyLocations } from '../hooks/useMyLocations';

const QUICK_TABS: { value: QuickTabValue; label: string }[] = [
  { value: 'all', label: 'All users' },
  { value: 'marketable', label: 'Marketable' },
  { value: 'new_week', label: 'New this week' },
];

const softBadgeSx = (color: string) => ({
  bgcolor: `${color}15`,
  color,
  fontWeight: 600,
  fontSize: '0.75rem',
  border: `1px solid ${color}40`,
  height: 'auto',
  '& .MuiChip-label': { px: 1.5, py: 0.75 },
  '& .MuiChip-icon': { ml: 0.75 },
});

function MarketableBadge({ marketable }: { marketable: boolean }) {
  const color = marketable ? colors.success : colors.error;
  return (
    <Chip
      size="small"
      icon={
        marketable ? (
          <CheckCircle sx={{ fontSize: '14px !important', color: `${color} !important` }} />
        ) : (
          <Cancel sx={{ fontSize: '14px !important', color: `${color} !important` }} />
        )
      }
      label={marketable ? 'Yes' : 'No'}
      sx={softBadgeSx(color)}
    />
  );
}

function OnlineStatusBadge({ isOnline }: { isOnline: boolean }) {
  const color = isOnline ? colors.success : colors.textSecondary;
  return (
    <Chip
      size="small"
      label={
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          {isOnline ? 'Online' : 'Offline'}
        </Box>
      }
      sx={softBadgeSx(color)}
    />
  );
}

// Column shows 3 tags only (active / upcoming / no active pack) Filters use the
// full 4-way packStatus from the API (expired vs never-had are split for filtering).
type PackDisplayStatus = 'active' | 'upcoming' | 'none';

function toPackDisplayStatus(status?: UserPackStatus): PackDisplayStatus {
  if (status === 'active' || status === 'upcoming') return status;
  return 'none';
}

// Colors match the Active/Upcoming pack chips on the Purchased Packs page,
// so the same status reads the same way everywhere in the admin panel.
const PACK_STATUS_CONFIG: Record<PackDisplayStatus, { label: string; color: string }> = {
  active: { label: 'Active pack', color: '#4CAF50' },
  upcoming: { label: 'Upcoming pack', color: '#7B1FA2' },
  none: { label: 'No active pack', color: colors.textSecondary },
};

function PackStatusBadge({ status }: { status?: UserPackStatus }) {
  const display = toPackDisplayStatus(status);
  const { label, color } = PACK_STATUS_CONFIG[display];
  return <Chip size="small" label={label} sx={softBadgeSx(color)} />;
}

type PackSummary = {
  _id: string;
  title: string;
  studioName?: string;
  classesLeft: number | null;
  totalClasses: number | null;
  expiry?: string;
  startDate?: string;
};

const packHoverCache = new Map<string, PackSummary[]>();

function isPackCurrentlyActive(p: any): boolean {
  const now = new Date();
  if (p.isFrozen) return false;
  if (p.startDate && new Date(p.startDate) > now) return false;
  const expiry = p.expiryDate ?? p.packEndDate ?? null;
  const isDateExpired = !!expiry && new Date(expiry) <= now;
  // Credits are deducted at booking, so upcoming booked sessions still count as
  // left — matches the backend's packStatus for the badge.
  const classesExhausted =
    !!p.totalClasses && (p.classesLeft ?? 0) + (p.pendingBookingsCount ?? 0) <= 0;
  return !isDateExpired && !classesExhausted;
}

function isPackUpcoming(p: any): boolean {
  const now = new Date();
  if (p.isFrozen) return false;
  if (!p.startDate) return false;
  return new Date(p.startDate) > now;
}

function formatPackDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatSessionsLeft(classesLeft: number | null, totalClasses: number | null) {
  if (classesLeft === null) return 'Unlimited';
  if (totalClasses) return `${classesLeft} / ${totalClasses} left`;
  return `${classesLeft} left`;
}

function formatTotalSessions(totalClasses: number | null) {
  if (totalClasses === null) return 'Unlimited sessions';
  return `${totalClasses} session${totalClasses !== 1 ? 's' : ''}`;
}

function PackHoverTooltipContent({
  packs,
  loading,
  variant,
}: {
  packs: PackSummary[] | null;
  loading: boolean;
  variant: 'active' | 'upcoming';
}) {
  const cfg = PACK_STATUS_CONFIG[variant];
  const emptyLabel = variant === 'active' ? 'No active packs found' : 'No upcoming packs found';
  const headerLabel = variant === 'active' ? 'Active pack' : 'Upcoming pack';

  if (loading) {
    return (
      <Box sx={{ p: 1.5, minWidth: 220 }}>
        <Skeleton width="60%" height={18} />
        <Skeleton width="90%" height={14} sx={{ mt: 1 }} />
        <Skeleton width="75%" height={14} sx={{ mt: 0.75 }} />
      </Box>
    );
  }

  if (!packs?.length) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5, minWidth: 240, maxWidth: 320 }}>
      <Box display="flex" alignItems="center" gap={0.75} mb={1}>
        <LocalOffer sx={{ fontSize: 16, color: cfg.color }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {headerLabel}{packs.length > 1 ? 's' : ''}
        </Typography>
        <Chip
          label={packs.length}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: `${cfg.color}18`,
            color: cfg.color,
          }}
        />
      </Box>
      {packs.map((pack, idx) => (
        <Box key={pack._id}>
          {idx > 0 && <Divider sx={{ my: 1 }} />}
          <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
            {pack.title}
          </Typography>
          {pack.studioName && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
              {pack.studioName}
            </Typography>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} mt={0.5}>
            {variant === 'active' ? (
              <Typography variant="caption" fontWeight={600} sx={{ color: cfg.color }}>
                {formatSessionsLeft(pack.classesLeft, pack.totalClasses)}
              </Typography>
            ) : (
              <Typography variant="caption" fontWeight={600} sx={{ color: cfg.color }}>
                {formatTotalSessions(pack.totalClasses)}
              </Typography>
            )}
            {variant === 'active' && pack.expiry && (
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                Exp {formatPackDate(pack.expiry)}
              </Typography>
            )}
            {variant === 'upcoming' && pack.startDate && (
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                Starts {formatPackDate(pack.startDate)}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function mapPackSummary(p: any): PackSummary {
  return {
    _id: p._id,
    title: p.title || p.pack?.title || 'Pack',
    studioName: typeof p.studio === 'object' ? p.studio?.studioName : undefined,
    classesLeft: typeof p.classesLeft === 'number' ? p.classesLeft + (p.pendingBookingsCount ?? 0) : null,
    totalClasses: p.totalClasses ?? p.pack?.totalClasses ?? null,
    expiry: p.expiryDate || p.packEndDate || undefined,
    startDate: p.startDate || undefined,
  };
}

function PackStatusCell({
  userId,
  status,
}: {
  userId: string;
  status?: UserPackStatus;
}) {
  const [packs, setPacks] = useState<PackSummary[] | null>(null);
  const [loading, setLoading] = useState(false);

  const loadPacks = useCallback(async () => {
    if (status !== 'active' && status !== 'upcoming') return;
    const cacheKey = `${userId}:${status}`;
    if (packHoverCache.has(cacheKey)) {
      setPacks(packHoverCache.get(cacheKey)!);
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.getUserPackHistory(userId, {
        status,
        page: 1,
        pageSize: 50,
      });
      const filterFn = status === 'active' ? isPackCurrentlyActive : isPackUpcoming;
      const summaries = (res.purchasedPacks || []).filter(filterFn).map(mapPackSummary);
      packHoverCache.set(cacheKey, summaries);
      setPacks(summaries);
    } catch {
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, [userId, status]);

  if (status !== 'active' && status !== 'upcoming') {
    return <PackStatusBadge status={status} />;
  }

  const cfg = PACK_STATUS_CONFIG[toPackDisplayStatus(status)];

  return (
    <Tooltip
      arrow
      enterDelay={400}
      onOpen={loadPacks}
      title={<PackHoverTooltipContent packs={packs} loading={loading} variant={status} />}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 0,
            maxWidth: 340,
            '& .MuiTooltip-arrow': { color: 'background.paper' },
          },
        },
      }}
    >
      <Box component="span" display="inline-flex">
        <Chip
          size="small"
          label={cfg.label}
          sx={{
            ...softBadgeSx(cfg.color),
            cursor: 'default',
            '&:hover': { bgcolor: `${cfg.color}22` },
          }}
        />
      </Box>
    </Tooltip>
  );
}

function parseListParam(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').map(v => v.trim()).filter(Boolean);
}

function parseAdvancedFilters(params: URLSearchParams): UserAdvancedFilters {
  const gender = params.get('gender');
  const marketable = params.get('marketable');
  const pack = params.get('pack');
  return {
    gender: gender === 'Female' || gender === 'Male' ? gender : null,
    marketable: marketable === 'yes' || marketable === 'no' ? marketable : null,
    pack:
      pack === 'active' || pack === 'upcoming' || pack === 'expired' || pack === 'none'
        ? pack
        : null,
    cities: parseListParam(params.get('city')),
  };
}

function countAdvancedFilters(filters: UserAdvancedFilters): number {
  return (
    (filters.gender ? 1 : 0) +
    (filters.marketable ? 1 : 0) +
    (filters.pack ? 1 : 0) +
    (filters.cities.length > 0 ? 1 : 0)
  );
}

// Columns/tabs hidden from studio owners — they must not see SweatSocial,
// cross-studio, or marketing data for whitelabel isolation.
const RESTRICTED_STUDIO_COLS = ['platform', 'marketable', 'studios', 'status'];

const UsersPage: React.FC = () => {
  const { admin } = useAuth();
  const isStudioRole = isStudioScopedRole(admin?.role);
  // Under "All locations" a removal must name the location it detaches the
  // member from — `__all__` is refused for writes.
  const { isAllLocations } = useMyLocations();
  const mustPickRemoveBranch = isStudioRole && isAllLocations;
  const [removeBranch, setRemoveBranch] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState<AdminUser[]>([]);
  // Member notes — the location's staff edit, super admins read every studio's.
  // Instructors add notes from the bookings page only, not here.
  const notesMode = admin?.role === 'instructor' ? null : notesModeFor(admin?.role);
  const { notesByUser, updateNote } = useUserNotes(users.map((u) => u._id), notesMode);
  const [noteUser, setNoteUser] = useState<{ _id: string; name?: string } | null>(null);
  const [usesPartnerApi, setUsesPartnerApi] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [studios, setStudios] = useState<any[]>([]);

  const searchTerm = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(searchTerm);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const skipSearchSyncRef = useRef(false);

  const rawTab = searchParams.get('tab');
  const quickTab: QuickTabValue =
    rawTab === 'marketable' || rawTab === 'new_week' ? rawTab : 'all';
  const platformFilter = searchParams.get('platform') || 'all';
  const sortDir = searchParams.get('sort') === 'joined_asc' ? 'asc' : 'desc';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Math.max(10, Math.min(100, Number(searchParams.get('pageSize')) || 25));

  const appliedFilters = useMemo(() => parseAdvancedFilters(searchParams), [searchParams]);
  const activeFilterCount = countAdvancedFilters(appliedFilters);

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFilters, setDraftFilters] = useState<UserAdvancedFilters>(EMPTY_USER_ADVANCED_FILTERS);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState<HTMLElement | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<UserColumnKey[]>(loadVisibleColumns);
  // For studio owners, force-hide the restricted columns regardless of saved prefs.
  const displayColumns = isStudioRole
    ? visibleColumns.filter(c => !RESTRICTED_STUDIO_COLS.includes(c as string))
    : visibleColumns;
  // The channel column is meaningful only where both channels are in use.
  const showAppColumn = displayColumns.includes('app') && usesPartnerApi;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuUser, setMenuUser] = useState<AdminUser | null>(null);
  const [studiosAnchorEl, setStudiosAnchorEl] = useState<HTMLElement | null>(null);
  const [studiosPopoverData, setStudiosPopoverData] = useState<AdminUser['associatedStudios']>([]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [deleteWarnings, setDeleteWarnings] = useState<UserRemoveWarnings>(
    emptyUserRemoveWarnings(),
  );
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [assignPackOpen, setAssignPackOpen] = useState(false);
  const [manualBookingOpen, setManualBookingOpen] = useState(false);
  const [flowUser, setFlowUser] = useState<AdminUser | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getUsers();
      setUsers(response.data || []);
      // Only a studio running BOTH channels has anything to read here; the
      // backend derives this from whether any member arrived via the partner
      // API. For an app-only studio the column would be a constant "App".
      setUsesPartnerApi(response.usesPartnerApi === true);
      packHoverCache.clear();
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    apiService.getStudios().then(res => setStudios(res.data || res.studios || [])).catch(() => {});
  }, [fetchUsers]);

  useEffect(() => {
    if (skipSearchSyncRef.current) {
      skipSearchSyncRef.current = false;
      return;
    }
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = false) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
        });
        if (resetPage) next.delete('page');
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      skipSearchSyncRef.current = true;
      updateParams({ q: value || null }, true);
    }, 300);
  };

  const platformOptions = useMemo(() => derivePlatformOptions(users), [users]);
  const cityOptions = useMemo(() => deriveCityOptions(users), [users]);
  const stats = useMemo(() => computeUserStats(users), [users]);

  const platformScopedUsers = useMemo(
    () => users.filter(u => userMatchesPlatform(u, platformFilter)),
    [users, platformFilter],
  );

  const quickTabCounts = useMemo(() => {
    const weekAgo = stats.weekAgo;
    return {
      all: platformScopedUsers.length,
      marketable: platformScopedUsers.filter(isUserMarketable).length,
      new_week: platformScopedUsers.filter(u => new Date(u.createdAt) >= weekAgo).length,
    };
  }, [platformScopedUsers, stats.weekAgo]);

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const weekAgo = stats.weekAgo;

    return platformScopedUsers
      .filter(user => {
        if (q) {
          const matchesSearch =
            user.name?.toLowerCase().includes(q) ||
            user.email?.toLowerCase().includes(q) ||
            user.phoneNumber?.includes(q) ||
            user.city?.toLowerCase().includes(q);
          if (!matchesSearch) return false;
        }

        if (quickTab === 'marketable' && !isUserMarketable(user)) return false;
        if (quickTab === 'new_week' && new Date(user.createdAt) < weekAgo) return false;

        if (appliedFilters.gender && user.gender !== appliedFilters.gender) return false;

        if (appliedFilters.marketable === 'yes' && !isUserMarketable(user)) return false;
        if (appliedFilters.marketable === 'no' && isUserMarketable(user)) return false;

        if (appliedFilters.pack && !userMatchesPackFilter(user, appliedFilters.pack)) return false;

        if (appliedFilters.cities.length > 0) {
          if (!user.city || !appliedFilters.cities.includes(user.city)) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        return sortDir === 'asc' ? diff : -diff;
      });
  }, [platformScopedUsers, searchTerm, quickTab, appliedFilters, sortDir, stats.weekAgo]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const rangeStart = filteredUsers.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, filteredUsers.length);

  useEffect(() => {
    if (page !== currentPage) {
      updateParams({ page: String(currentPage) });
    }
  }, [page, currentPage, updateParams]);

  const navigateToUser = (userId: string) => {
    navigate(`/users/${userId}`, {
      state: { from: buildCurrentPath(location.pathname, location.search) },
    });
  };

  const openFilterPanel = (event: React.MouseEvent<HTMLElement>) => {
    setDraftFilters(appliedFilters);
    setFilterAnchorEl(event.currentTarget);
  };

  const applyFilters = () => {
    updateParams(
      {
        gender: draftFilters.gender,
        marketable: draftFilters.marketable,
        pack: draftFilters.pack,
        city: draftFilters.cities.length ? draftFilters.cities.join(',') : null,
      },
      true,
    );
    setFilterAnchorEl(null);
  };

  const clearDraftFilters = () => setDraftFilters(EMPTY_USER_ADVANCED_FILTERS);

  const userFilterSections = useMemo((): AdvancedFilterSection[] => {
    const toggleSingle = <K extends 'gender' | 'marketable' | 'pack'>(key: K, value: NonNullable<UserAdvancedFilters[K]>) => {
      setDraftFilters(prev => ({
        ...prev,
        [key]: prev[key] === value ? null : value,
      }));
    };

    return [
      {
        type: 'single-select-toggle',
        label: 'Gender',
        options: [
          { value: 'Female', label: 'Female' },
          { value: 'Male', label: 'Male' },
        ],
        value: draftFilters.gender,
        onToggle: value => toggleSingle('gender', value as GenderFilter),
      },
      {
        type: 'single-select-toggle',
        label: 'Marketable',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        value: draftFilters.marketable,
        onToggle: value => toggleSingle('marketable', value as MarketableFilter),
      },
      {
        type: 'single-select-toggle',
        label: 'Pack',
        options: [],
        value: draftFilters.pack,
        onToggle: value => toggleSingle('pack', value as UserPackFilter),
        rows: [
          [
            { value: 'active', label: 'Active' },
            { value: 'upcoming', label: 'Upcoming' },
          ],
          [
            { value: 'expired', label: 'Expired' },
            { value: 'none', label: 'No pack' },
          ],
        ],
      },
      {
        type: 'multi-select-pills',
        label: 'City',
        options: cityOptions,
        selected: draftFilters.cities,
        onToggle: city => {
          setDraftFilters(prev => ({
            ...prev,
            cities: prev.cities.includes(city)
              ? prev.cities.filter(c => c !== city)
              : [...prev.cities, city],
          }));
        },
        emptyText: 'No cities in current data',
      },
    ];
  }, [draftFilters, cityOptions]);

  const toggleColumn = (column: UserColumnKey) => {
    setVisibleColumns(prev => {
      const next = prev.includes(column) ? prev.filter(c => c !== column) : [...prev, column];
      saveVisibleColumns(next);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = paginatedUsers.map(u => u._id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: AdminUser) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuUser(null);
  };

  const openDeleteDialog = async (user: AdminUser) => {
    setUserToDelete(user);
    setRemoveBranch('');
    try {
      setDeleteWarnings(await apiService.getUserRemoveWarnings(user._id));
    } catch {
      setDeleteWarnings(emptyUserRemoveWarnings());
    }
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    if (mustPickRemoveBranch && !removeBranch) return;
    setIsDeleting(true);
    try {
      await apiService.deleteUser(userToDelete._id, mustPickRemoveBranch ? removeBranch : undefined);
      if (isStudioRole) {
        // Studio-scoped view: removing detaches from THIS studio only — the
        // user no longer matches this studio's filter, so drop the row.
        setUsers(prev => prev.filter(u => u._id !== userToDelete._id));
      } else {
        // Super-admin: delete is non-destructive now, the record stays in
        // the DB — reflect the new "Removed by Admin" state instead of
        // hiding the row, since a refetch would still show them.
        setUsers(prev =>
          prev.map(u =>
            u._id === userToDelete._id ? { ...u, registrationSource: 'removed_by_admin' } : u,
          ),
        );
      }
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(userToDelete._id);
        return next;
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (mustPickRemoveBranch && !removeBranch) return;
    setIsDeleting(true);
    try {
      await Promise.all(
        ids.map(id => apiService.deleteUser(id, mustPickRemoveBranch ? removeBranch : undefined)),
      );
      if (isStudioRole) {
        setUsers(prev => prev.filter(u => !selectedIds.has(u._id)));
      } else {
        setUsers(prev =>
          prev.map(u =>
            selectedIds.has(u._id) ? { ...u, registrationSource: 'removed_by_admin' } : u,
          ),
        );
      }
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to delete selected users');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkToggleMarketable = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const selectedUsers = users.filter(u => ids.includes(u._id));
    const allMarketable = selectedUsers.every(isUserMarketable);
    try {
      await apiService.bulkToggleUserMarketable(ids, !allMarketable);
      setUsers(prev =>
        prev.map(u =>
          ids.includes(u._id) ? { ...u, isMarketable: !allMarketable } : u,
        ),
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update marketable status');
    }
  };

  const exportRows = (rows: AdminUser[]) => {
    const csvRows = [
      ['Full Name', 'Email', 'Phone', 'City', 'Gender', 'Age', 'Platform', 'Marketable', 'Status', 'Joined', 'Studios'],
      ...rows.map(u => [
        u.name || '',
        u.email || '',
        u.phoneNumber || '',
        u.city || '',
        u.gender || '',
        u.age?.toString() || '',
        getRemovedByLabel(u) ?? getPlatformDisplay(u).label,
        isUserMarketable(u) ? 'Yes' : 'No',
        u.isOnline ? 'Online' : 'Offline',
        formatJoinedDate(u.createdAt),
        String(u.associatedStudios?.length || 0),
      ]),
    ];
    const csvContent = csvRows
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderStatCard = (
    title: string,
    value: string | number,
    subtitle: string,
    icon: React.ReactNode,
    accent: string,
  ) => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
              {subtitle}
            </Typography>
          </Box>
          <Box sx={{ color: accent, opacity: 0.9 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={280} height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={420} height={28} sx={{ mb: 3 }} />
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mb={3}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={110} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={520} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Users Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and monitor all user accounts across your platforms.
          </Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => exportRows(filteredUsers)}
            disabled={filteredUsers.length === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Create User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchUsers}>
              Retry
            </Button>
          }
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, 1fr)' }} gap={2} mb={3}>
        {renderStatCard(
          'Total Users',
          stats.total,
          `${stats.pctVsLastMonth >= 0 ? '+' : ''}${stats.pctVsLastMonth.toFixed(1)}% vs. last month`,
          <People />,
          colors.success,
        )}
        {renderStatCard(
          'Complete Profiles',
          stats.complete,
          `${stats.completePct}% of all users`,
          <CheckCircle />,
          colors.info,
        )}
        {renderStatCard(
          'New This Month',
          stats.newThisMonth,
          `+${stats.newSinceMonthStart} since ${stats.monthStartLabel}`,
          <PersonAdd />,
          colors.warning,
        )}
      </Box>

      <Tabs
        value={quickTab}
        onChange={(_, value: QuickTabValue) => updateParams({ tab: value === 'all' ? null : value }, true)}
        sx={{ mb: 2, minHeight: 40 }}
        TabIndicatorProps={{ sx: { height: 3, borderRadius: 2 } }}
      >
        {(isStudioRole ? QUICK_TABS.filter(t => t.value !== 'marketable') : QUICK_TABS).map(tab => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={`${tab.label} ${quickTabCounts[tab.value]}`}
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }}
          />
        ))}
      </Tabs>

      <Card sx={{ mb: 1.5, borderRadius: 2 }}>
        <CardContent sx={{ p: 2.5, pb: 2 }}>
          <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              fullWidth
              placeholder="Search by name, email, phone, city..."
              value={searchInput}
              onChange={handleSearchChange}
              size="small"
              sx={{ flex: 1, minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Badge badgeContent={activeFilterCount} color="primary" invisible={activeFilterCount === 0}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={openFilterPanel}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Filters
              </Button>
            </Badge>
            <Button
              variant="outlined"
              startIcon={<ViewColumn />}
              onClick={e => setColumnsAnchorEl(e.currentTarget)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Columns
            </Button>
          </Box>

          {/* Platform/source chips (SweatSocial, Website, other studios) — hidden
              from studio owners to avoid leaking cross-studio / SweatSocial data. */}
          {!isStudioRole && (
          <Box
            display="flex"
            gap={1}
            flexWrap="nowrap"
            alignItems="center"
            sx={{
              mt: 1.5,
              overflowX: 'auto',
              pb: 0.5,
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'action.disabled' },
            }}
          >
            {platformOptions.map(opt => {
              const count = users.filter(u => userMatchesPlatform(u, opt.value)).length;
              const active = platformFilter === opt.value;
              return (
                <Chip
                  key={opt.value}
                  label={`${opt.label} ${count}`}
                  onClick={() => updateParams({ platform: opt.value === 'all' ? null : opt.value }, true)}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    fontWeight: 700,
                    cursor: 'pointer',
                    bgcolor: active ? opt.color : `${opt.color}14`,
                    color: active ? '#fff' : opt.color,
                    border: `1px solid ${opt.color}40`,
                    height: 'auto',
                    '& .MuiChip-label': { px: 1.5, py: 0.75 },
                  }}
                />
              );
            })}
          </Box>
          )}
        </CardContent>
      </Card>

      {selectedIds.size > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            bgcolor: `${colors.primary}10`,
            border: `1px solid ${colors.primary}30`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" fontWeight={700} color="primary">
            {selectedIds.size} selected
          </Typography>
          {!isStudioRole && (
            <Button size="small" startIcon={<LocalOffer />} onClick={handleBulkToggleMarketable} sx={{ textTransform: 'none' }}>
              Toggle marketable
            </Button>
          )}
          <Button
            size="small"
            startIcon={<FileDownload />}
            onClick={() => exportRows(users.filter(u => selectedIds.has(u._id)))}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<Delete />}
            onClick={() => {
              setRemoveBranch('');
              setBulkDeleteDialogOpen(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
          <Box flex={1} />
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ textTransform: 'none' }}>
            Clear
          </Button>
        </Paper>
      )}

      <Card sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: colors.backgroundTertiary }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      paginatedUsers.some(u => selectedIds.has(u._id)) &&
                      !paginatedUsers.every(u => selectedIds.has(u._id))
                    }
                    checked={paginatedUsers.length > 0 && paginatedUsers.every(u => selectedIds.has(u._id))}
                    onChange={toggleSelectAllVisible}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>NAME</TableCell>
                {displayColumns.includes('phone') && <TableCell sx={{ fontWeight: 700 }}>PHONE</TableCell>}
                {displayColumns.includes('age') && <TableCell sx={{ fontWeight: 700 }}>AGE</TableCell>}
                {displayColumns.includes('gender') && <TableCell sx={{ fontWeight: 700 }}>GENDER</TableCell>}
                {displayColumns.includes('city') && <TableCell sx={{ fontWeight: 700 }}>CITY</TableCell>}
                {displayColumns.includes('platform') && <TableCell sx={{ fontWeight: 700 }}>PLATFORM</TableCell>}
                {showAppColumn && (
                  <TableCell sx={{ fontWeight: 700 }}>
                    <Tooltip
                      arrow
                      title="Which channel each member joined through — the studio's app, or its website via the partner API. Shown only for studios running both; where every member comes from the app the column would be a constant."
                    >
                      <Box component="span" sx={{ borderBottom: '1px dotted', cursor: 'help' }}>APP</Box>
                    </Tooltip>
                  </TableCell>
                )}
                {displayColumns.includes('marketable') && <TableCell sx={{ fontWeight: 700 }}>MARKETABLE</TableCell>}
                {displayColumns.includes('status') && <TableCell sx={{ fontWeight: 700 }}>STATUS</TableCell>}
                {displayColumns.includes('joined') && (
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active
                      direction={sortDir}
                      onClick={() =>
                        updateParams({ sort: sortDir === 'desc' ? 'joined_asc' : null })
                      }
                    >
                      JOINED
                    </TableSortLabel>
                  </TableCell>
                )}
                {displayColumns.includes('studios') && <TableCell sx={{ fontWeight: 700 }}>STUDIOS</TableCell>}
                {displayColumns.includes('pack') && <TableCell sx={{ fontWeight: 700 }}>PACK</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12}>
                    <Box textAlign="center" py={6}>
                      <Typography variant="h6" gutterBottom>
                        No users found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Try adjusting your search or filters.
                      </Typography>
                      <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
                        Create User
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map(user => {
                  const platform = getPlatformDisplay(user);
                  const removedByLabel = getRemovedByLabel(user);
                  const marketable = isUserMarketable(user);
                  return (
                    <TableRow key={user._id} hover selected={selectedIds.has(user._id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(user._id)}
                          onChange={() => toggleSelectOne(user._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.25}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigateToUser(user._id)}
                        >
                          <Avatar
                            src={user.image}
                            sx={{ width: 36, height: 36, bgcolor: getAvatarColor(user.name), fontSize: '0.8rem' }}
                          >
                            {getUserInitials(user.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={700}>
                              {user.name || 'No Name'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email || 'No email'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {displayColumns.includes('phone') && (
                        <TableCell>{user.phoneNumber || '—'}</TableCell>
                      )}
                      {displayColumns.includes('age') && <TableCell>{user.age ?? '—'}</TableCell>}
                      {displayColumns.includes('gender') && <TableCell>{user.gender || '—'}</TableCell>}
                      {displayColumns.includes('city') && <TableCell>{user.city || '—'}</TableCell>}
                      {displayColumns.includes('platform') && (
                        <TableCell>
                          {removedByLabel ? (
                            <Chip
                              size="small"
                              label={
                                <Box display="flex" alignItems="center" gap={0.75}>
                                  <Box
                                    sx={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: '50%',
                                      bgcolor: colors.warning,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Box
                                    component="span"
                                    sx={{
                                      fontStyle: 'italic',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 160,
                                    }}
                                  >
                                    {removedByLabel}
                                  </Box>
                                </Box>
                              }
                              sx={softBadgeSx(colors.warning)}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label={
                                <Box display="flex" alignItems="center" gap={0.75}>
                                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: platform.color }} />
                                  {platform.label}
                                </Box>
                              }
                              sx={softBadgeSx(platform.color)}
                            />
                          )}
                        </TableCell>
                      )}
                      {showAppColumn && (
                        <TableCell>
                          {(() => {
                            // The CHANNEL a member arrived by — nothing more.
                            //
                            // Decided by registrationSource alone. An FCM token
                            // was tried and is wrong for this: a token proves
                            // the app is installed, but its absence proves
                            // nothing (tokens clear on sign-out; accounts
                            // predating per-app tokens never had one). It made
                            // every Coreline row read "No app yet" while most of
                            // those members do use the app.
                            const viaPartner = user.registrationSource === 'partner_api';
                            return (
                              <Tooltip
                                arrow
                                title={
                                  viaPartner
                                    ? 'Joined through the studio website (partner API).'
                                    : 'Joined through the studio app.'
                                }
                              >
                                <Chip
                                  size="small"
                                  label={viaPartner ? 'Website' : 'App'}
                                  sx={softBadgeSx(viaPartner ? colors.info : colors.success)}
                                />
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      )}
                      {displayColumns.includes('marketable') && (
                        <TableCell>
                          <MarketableBadge marketable={marketable} />
                        </TableCell>
                      )}
                      {displayColumns.includes('status') && (
                        <TableCell>
                          <OnlineStatusBadge isOnline={user.isOnline} />
                        </TableCell>
                      )}
                      {displayColumns.includes('joined') && (
                        <TableCell>{formatJoinedDate(user.createdAt)}</TableCell>
                      )}
                      {displayColumns.includes('studios') && (
                        <TableCell>
                          {(user.associatedStudios?.length ?? 0) === 0 ? (
                            '—'
                          ) : (
                            <Chip
                              size="small"
                              icon={<Business sx={{ fontSize: '14px !important' }} />}
                              label={`${user.associatedStudios!.length} studio${user.associatedStudios!.length > 1 ? 's' : ''}`}
                              onClick={e => {
                                e.stopPropagation();
                                setStudiosPopoverData(user.associatedStudios);
                                setStudiosAnchorEl(e.currentTarget);
                              }}
                              sx={{ cursor: 'pointer', fontWeight: 600 }}
                            />
                          )}
                        </TableCell>
                      )}
                      {displayColumns.includes('pack') && (
                        <TableCell>
                          <PackStatusCell userId={user._id} status={user.packStatus} />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => navigateToUser(user._id)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditUser(user);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(user)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={e => handleMenuOpen(e, user)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1.5}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="body2" color="text.secondary">
            {filteredUsers.length === 0
              ? '0 users'
              : `${rangeStart}-${rangeEnd} of ${filteredUsers.length}`}
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, p) => updateParams({ page: String(p) })}
            shape="rounded"
            color="primary"
          />
        </Box>
      </Card>

      <AdvancedFiltersPopover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        sections={userFilterSections}
        onApply={applyFilters}
        onClearAll={clearDraftFilters}
        width={360}
      />

      <Popover
        open={Boolean(columnsAnchorEl)}
        anchorEl={columnsAnchorEl}
        onClose={() => setColumnsAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Visible columns
          </Typography>
          {(Object.keys(USER_COLUMN_LABELS) as UserColumnKey[])
            .filter(column => !isStudioRole || !RESTRICTED_STUDIO_COLS.includes(column as string))
            .map(column => (
            <Box key={column} display="flex" alignItems="center">
              <Checkbox
                checked={displayColumns.includes(column)}
                onChange={() => toggleColumn(column)}
                size="small"
              />
              <Typography variant="body2">{USER_COLUMN_LABELS[column]}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (menuUser) setEditUser(menuUser);
            handleMenuClose();
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit user
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuUser) {
              setFlowUser(menuUser);
              setAssignPackOpen(true);
            }
            handleMenuClose();
          }}
        >
          Assign a pack
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuUser) {
              setFlowUser(menuUser);
              setManualBookingOpen(true);
            }
            handleMenuClose();
          }}
        >
          Create manual booking
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuUser) {
              navigate(`/users/${menuUser._id}?tab=bookings`, {
                state: { from: buildCurrentPath(location.pathname, location.search) },
              });
            }
            handleMenuClose();
          }}
        >
          Booking history
        </MenuItem>
        {(() => {
          const label = menuUser ? noteMenuLabel(notesMode, (notesByUser[menuUser._id] || []).length > 0) : null;
          return label && menuUser ? (
            <MenuItem
              onClick={() => {
                setNoteUser({ _id: menuUser._id, name: menuUser.name ?? undefined });
                handleMenuClose();
              }}
            >
              {label}
            </MenuItem>
          ) : null;
        })()}
        <Divider />
        <MenuItem
          sx={{ color: colors.error }}
          onClick={() => {
            if (menuUser) {
              openDeleteDialog(menuUser);
            }
            handleMenuClose();
          }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete user
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(studiosAnchorEl)}
        anchorEl={studiosAnchorEl}
        onClose={() => setStudiosAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 240, borderRadius: 2 } }}
      >
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          Associated Studios
        </Typography>
        {(studiosPopoverData || []).map((entry, idx) => {
          const studioName =
            typeof entry.studio === 'object' ? entry.studio?.studioName : 'Unknown Studio';
          return (
            <Box key={idx} py={0.5}>
              <Typography variant="body2" fontWeight={600}>
                {studioName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                via {entry.source}
              </Typography>
            </Box>
          );
        })}
      </Popover>

      <UserNoteDialog
        open={!!noteUser}
        user={noteUser}
        notes={noteUser ? notesByUser[noteUser._id] || [] : []}
        // A note belongs to one location: the switcher's. Under "All
        // locations" there isn't one, so it's read-only until one is picked.
        targetStudioId={notesMode === 'studio' ? currentNoteStudioId() : null}
        hint={notesMode === 'studio' ? 'Choose a location in the switcher to add or edit a note.' : undefined}
        onClose={() => setNoteUser(null)}
        onChange={updateNote}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{isStudioRole ? 'Remove from studio' : 'Delete user'}</DialogTitle>
        <DialogContent>
          {userToDelete ? (
            <RemoveUserConfirmMessage
              name={userToDelete.name || 'This user'}
              warnings={deleteWarnings}
              isStudioRole={isStudioRole}
            />
          ) : null}
          {mustPickRemoveBranch && (
            <Box sx={{ mt: 2 }}>
              <BranchSelect
                value={removeBranch}
                onChange={(v) => setRemoveBranch(v || '')}
                label="Remove from location"
                minRole="manager"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteUser}
            disabled={isDeleting || (mustPickRemoveBranch && !removeBranch)}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>{isStudioRole ? 'Remove from studio' : 'Delete selected users'}</DialogTitle>
        <DialogContent>
          <Typography>
            {isStudioRole
              ? `Remove ${selectedIds.size} member${selectedIds.size !== 1 ? 's' : ''} from your studio?`
              : `Are you sure you want to delete ${selectedIds.size} selected user${selectedIds.size !== 1 ? 's' : ''}? Their data stays on record, but they'll need to create their account again to use SweatSocial.`}
          </Typography>
          {mustPickRemoveBranch && (
            <Box sx={{ mt: 2 }}>
              <BranchSelect
                value={removeBranch}
                onChange={(v) => setRemoveBranch(v || '')}
                label="Remove from location"
                minRole="manager"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmBulkDelete}
            disabled={isDeleting || (mustPickRemoveBranch && !removeBranch)}
          >
            {isDeleting ? 'Deleting…' : 'Delete all'}
          </Button>
        </DialogActions>
      </Dialog>

      <UserFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={fetchUsers}
      />
      <UserFormDialog
        open={Boolean(editUser)}
        onClose={() => setEditUser(null)}
        onSuccess={fetchUsers}
        user={editUser}
      />

      <ManualPackAssignDialog
        open={assignPackOpen}
        onClose={() => {
          setAssignPackOpen(false);
          setFlowUser(null);
        }}
        onSuccess={() => {
          setAssignPackOpen(false);
          setFlowUser(null);
        }}
        studios={studios}
        prefilledUser={
          flowUser
            ? {
                _id: flowUser._id,
                name: flowUser.name || undefined,
                email: flowUser.email || undefined,
                phoneNumber: flowUser.phoneNumber || undefined,
                gender: flowUser.gender,
              }
            : undefined
        }
      />

      <ManualBookingDialog
        open={manualBookingOpen}
        onClose={() => {
          setManualBookingOpen(false);
          setFlowUser(null);
        }}
        onSuccess={() => {
          setManualBookingOpen(false);
          setFlowUser(null);
        }}
        studios={studios}
        prefilledUser={
          flowUser
            ? {
                _id: flowUser._id,
                name: flowUser.name || undefined,
                email: flowUser.email || undefined,
                phoneNumber: flowUser.phoneNumber || undefined,
              }
            : undefined
        }
      />
    </Box>
  );
};

export default UsersPage;
